import { Injectable, inject } from '@angular/core';
import { LocalStorageService, sha256 } from '@reconcept-platform/shared/util';
import { type Observable } from 'rxjs';
import { type AuthTokens } from '../auth.api';
import { type AuthTokenService } from './auth-token-service.interface';

@Injectable({
  providedIn: 'root',
})
export class WebAuthTokenService implements AuthTokenService {
  private storage = inject(LocalStorageService);

  async clear(key: string): Promise<void> {
    this.storage.clear(key);
    return Promise.resolve();
  }

  async store(key: string, value: string): Promise<void> {
    this.storage.store(key, value);
    return Promise.resolve();
  }

  async retrieve(key: string): Promise<string> {
    return new Promise((resolve) => resolve(this.storage.retrieve(key)));
  }

  async hasAuthToken(): Promise<boolean> {
    return !!(await this.retrieveAccessToken());
  }

  async storeAccessToken(sessionToken: string): Promise<void> {
    this.storage.store('access_token', sessionToken);
    return Promise.resolve();
  }

  async retrieveAccessToken(): Promise<string> {
    return new Promise((resolve) => resolve(this.storage.retrieve('access_token') || null));
  }

  async storeRefreshToken(token: string) {
    this.storage.store('refresh_token', token);
    return Promise.resolve();
  }

  async retrieveRefreshToken(): Promise<string> {
    return new Promise((resolve) => resolve(this.storage.retrieve('refresh_token') || null));
  }

  async clearStorage() {
    this.storage.clear('access_token');
    this.storage.clear('refresh_token');
    await this.removeInviteToken();
    return Promise.resolve();
  }

  // null safe
  async setTokens(tokens: AuthTokens) {
    // store tokens
    await this.storeAccessToken(tokens?.accessToken);
    await this.storeRefreshToken(tokens?.refreshToken);
  }

  async hasInviteToken(): Promise<boolean> {
    const token = this.storage.retrieve('invite-token');
    return Promise.resolve(!!token);
  }

  async removeInviteToken(): Promise<void> {
    this.storage.clear('invite-token');
    return Promise.resolve();
  }

  async setInviteToken(token: string): Promise<void> {
    this.storage.store('invite-token', token);
    return Promise.resolve();
  }

  async setViewerId(viewerId: string): Promise<void> {
    this.storage.store('sv', await sha256(viewerId));
    return Promise.resolve();
  }

  observeViewerId(): Observable<string | null> {
    return this.storage.observe<string>('sv', { onlyOtherTabs: true });
  }
}
